import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { normalizeChoices, reorderChoices } from '../utils/helpers';
import { useFormState } from '../contexts/FormStateContext';
import styles from './fields.module.css';

export interface DropdownFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const DropdownField: React.FC<DropdownFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const formState = useFormState();
  const error = errors[element.name];
  const validationError = formState.validationErrors[element.name];

  const normalizedChoices = normalizeChoices(element.choices);
  const choices = reorderChoices(normalizedChoices, element.choicesOrder);

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
        htmlFor={element.name}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue || ''}
        render={({ field }) => (
          <select
            {...field}
            id={element.name}
            className={`${styles.input} ${styles.select} ${
              error || validationError ? styles.error : ''
            }`}
            disabled={element.readOnly}
            onChange={(e) => {
              field.onChange(e.target.value);
              formState.markFieldAsTouched(element.name);
              if (validationError) {
                formState.clearValidationError(element.name);
              }
            }}
            onBlur={() => {
              field.onBlur();
              formState.markFieldAsTouched(element.name);
            }}
          >
            <option value="">
              {element.placeholder || 'Selecione uma opção'}
            </option>
            {choices.map((choice, index) => (
              <option key={index} value={choice.value}>
                {choice.text}
              </option>
            ))}
          </select>
        )}
      />
      {(error || validationError) && (
        <p className={styles.errorMessage}>
          {(error?.message as string) || validationError}
        </p>
      )}
    </div>
  );
};

export default DropdownField;
